import type { Dispatch, SetStateAction } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import type { ISubDistrict } from 'features/Address'
import type { AddressOptionKey, BranchForForm, Field, OptionProps } from 'types'

import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dropdown, Loader } from 'components'
import * as utils from 'helpers/getAddress'

interface IInputSubDistrict {
  subDistricts: ISubDistrict[]
  isSubDistrictsLoading: boolean
  isSubDistrictChecking: boolean
  setIsSubDistrictChecking: Dispatch<SetStateAction<boolean>>
  field: ControllerRenderProps
  handleOptions: (
    districts: ISubDistrict[],
    name: Field,
  ) => OptionProps<AddressOptionKey>[]
  handleSelected: <T extends { [label: string]: string; id: string }>(
    selectedValue: T,
    name: Field,
  ) => OptionProps<AddressOptionKey> | undefined
  handleSubDistrictChange: (option: OptionProps) => void
}

export const InputSubDistrict: React.FC<IInputSubDistrict> = ({
  subDistricts,
  isSubDistrictsLoading,
  isSubDistrictChecking,
  setIsSubDistrictChecking,
  field,
  handleOptions,
  handleSelected,
  handleSubDistrictChange,
}) => {
  const { errors, watch, trigger } = useFormContext<BranchForForm>()

  const { isSameAsCompany, district, subDistrict } = watch([
    'isSameAsCompany',
    'district',
    'subDistrict',
  ])
  const { t } = useTranslation(['common'])

  const onChange = (item: OptionProps<AddressOptionKey>) => {
    field.onChange(item.key)
    handleSubDistrictChange(item)
  }

  const onBlur = async () => {
    setIsSubDistrictChecking(true)
    await trigger('subDistrict')
    setIsSubDistrictChecking(false)
  }

  const isError = utils.handleIsError(
    isSameAsCompany,
    subDistrict.id,
    Boolean(errors.subDistrict),
  )

  return (
    <Dropdown
      placeholder={t('common:address.subdistrict.placeholder')}
      options={handleOptions(subDistricts, 'subDistrict')}
      selected={handleSelected(field.value, 'subDistrict')}
      disabled={Boolean(!district.id) || isSameAsCompany}
      onChange={onChange}
      onBlur={onBlur}
      isError={isError}
      withIcon={(isSubDistrictsLoading || isSubDistrictChecking) && <Loader />}
      fullWidth
      ref={field.ref}
    />
  )
}
